import { useMemo } from 'react';
import { generatePath } from 'react-router-dom';

import {
  CarriageGroupIcon,
  ClassifiersIcon,
  ControlTransportParameterIcon,
  IndicatorsIcon,
  LocomotiveIcon,
  PbgIcon,
  SummaryCalculationsIcon,
  TrainIcon,
} from '@components/icons';

import {
  MnemonicGroupsEnum,
  TrainStatusMnemonicEnum,
} from '@constants/apiEnums';
import { ROUTES } from '@constants/routes';

import { useMnemonics } from '@hooks/useMnemonics';

import { EAppRouteGroup, EAppRouteStatus } from '@utils/enums';

import type { MenuExtendedItem } from '../types';

export const GetSidebarBodyMenu = (): MenuExtendedItem[] => {
  const { getDictionary } = useMnemonics();
  const trainStatusDictionary = useMemo(
    () => getDictionary({ groupName: MnemonicGroupsEnum.approvalStatus }),
    [getDictionary],
  );

  const titles = {
    planning:
      trainStatusDictionary[TrainStatusMnemonicEnum.planning].mnemonic_rus,
    approval:
      trainStatusDictionary[TrainStatusMnemonicEnum.approval].mnemonic_rus,
    approved:
      trainStatusDictionary[TrainStatusMnemonicEnum.approved].mnemonic_rus,
  };

  return [
    {
      title: 'Поезда',
      icon: <TrainIcon />,
      link: generatePath(ROUTES.TRAINS, {
        group: EAppRouteGroup.GroupInputScheme,
        status: EAppRouteStatus.StatusPlanning,
      }),
      route: ROUTES.TRAINS,
      subItems: [
        {
          title: titles.planning,
          link: generatePath(ROUTES.TRAINS, {
            group: EAppRouteGroup.GroupInputScheme,
            status: EAppRouteStatus.StatusPlanning,
          }),
          route: ROUTES.TRAINS,
        },
        {
          title: titles.approval,
          link: generatePath(ROUTES.TRAINS, {
            group: EAppRouteGroup.GroupInputScheme,
            status: EAppRouteStatus.StatusApproval,
          }),
          route: ROUTES.TRAINS,
        },
        {
          title: titles.approved,
          link: generatePath(ROUTES.TRAINS, {
            group: EAppRouteGroup.GroupInputScheme,
            status: EAppRouteStatus.StatusApproved,
          }),
          route: ROUTES.TRAINS,
        },
      ],
    },
    {
      title: 'Составы',
      icon: <CarriageGroupIcon />,
      link: generatePath(ROUTES.CARRIAGE_GROUPS, {
        group: EAppRouteGroup.GroupSchedules,
        status: EAppRouteStatus.StatusPlanning,
      }),
      route: ROUTES.CARRIAGE_GROUPS,
      subItems: [
        {
          title: titles.planning,
          link: generatePath(ROUTES.CARRIAGE_GROUPS, {
            group: EAppRouteGroup.GroupSchedules,
            status: EAppRouteStatus.StatusPlanning,
          }),
          route: ROUTES.CARRIAGE_GROUPS,
        },
        {
          title: titles.approval,
          link: generatePath(ROUTES.CARRIAGE_GROUPS, {
            group: EAppRouteGroup.GroupSchedules,
            status: EAppRouteStatus.StatusApproval,
          }),
          route: ROUTES.CARRIAGE_GROUPS,
        },
        {
          title: titles.approved,
          link: generatePath(ROUTES.CARRIAGE_GROUPS, {
            group: EAppRouteGroup.GroupSchedules,
            status: EAppRouteStatus.StatusApproved,
          }),
          route: ROUTES.CARRIAGE_GROUPS,
        },
      ],
    },
    {
      title: 'Прицепные группы',
      icon: <PbgIcon />,
      link: generatePath(ROUTES.TRAILED_DIRECT_GROUPS, {
        group: EAppRouteGroup.GroupInputScheme,
        status: EAppRouteStatus.StatusPlanning,
      }),
      route: ROUTES.TRAILED_DIRECT_GROUPS,
      subItems: [
        {
          title: titles.planning,
          link: generatePath(ROUTES.TRAILED_DIRECT_GROUPS, {
            group: EAppRouteGroup.GroupInputScheme,
            status: EAppRouteStatus.StatusPlanning,
          }),
          route: ROUTES.TRAILED_DIRECT_GROUPS,
        },
        {
          title: titles.approval,
          link: generatePath(ROUTES.TRAILED_DIRECT_GROUPS, {
            group: EAppRouteGroup.GroupInputScheme,
            status: EAppRouteStatus.StatusApproval,
          }),
          route: ROUTES.TRAILED_DIRECT_GROUPS,
        },
        {
          title: titles.approved,
          link: generatePath(ROUTES.TRAILED_DIRECT_GROUPS, {
            group: EAppRouteGroup.GroupInputScheme,
            status: EAppRouteStatus.StatusApproved,
          }),
          route: ROUTES.TRAILED_DIRECT_GROUPS,
        },
      ],
    },
    {
      title: 'Локомотивное обслуживание',
      icon: <LocomotiveIcon />,
      link: generatePath(ROUTES.LOCOMOTIVE_AREA, {
        status: EAppRouteStatus.StatusPlanning,
      }),
      route: ROUTES.LOCOMOTIVE_AREA,
      subItems: [
        {
          title: titles.planning,
          link: generatePath(ROUTES.LOCOMOTIVE_AREA, {
            status: EAppRouteStatus.StatusPlanning,
          }),
          route: ROUTES.LOCOMOTIVE_AREA,
        },
        {
          title: titles.approval,
          link: generatePath(ROUTES.LOCOMOTIVE_AREA, {
            status: EAppRouteStatus.StatusApproval,
          }),
          route: ROUTES.LOCOMOTIVE_AREA,
        },
        {
          title: titles.approved,
          link: generatePath(ROUTES.LOCOMOTIVE_AREA, {
            status: EAppRouteStatus.StatusApproved,
          }),
          route: ROUTES.LOCOMOTIVE_AREA,
        },
      ],
    },
    {
      title: 'Сводные показатели',
      icon: <SummaryCalculationsIcon />,
      link: ROUTES.SUMMARY_CALCULATIONS,
      route: ROUTES.SUMMARY_CALCULATIONS,
      subItems: [
        {
          title: 'По поезду',
          link: ROUTES.SUMMARY_CALCULATIONS_TRAIN,
          route: ROUTES.SUMMARY_CALCULATIONS_TRAIN,
        },
        {
          title: 'По локомотиву',
          link: ROUTES.SUMMARY_CALCULATIONS_LOCOMOTIVE,
          route: ROUTES.SUMMARY_CALCULATIONS_LOCOMOTIVE,
        },
        {
          title: 'По МВПС',
          link: ROUTES.SUMMARY_CALCULATIONS_MVPS,
          route: ROUTES.SUMMARY_CALCULATIONS_MVPS,
        },
        {
          title: 'По инфраструктуре',
          link: ROUTES.SUMMARY_CALCULATIONS_INFRASTRUCTURE,
          route: ROUTES.SUMMARY_CALCULATIONS_INFRASTRUCTURE,
        },
      ],
    },
    {
      title: 'Расчеты показателей',
      icon: <IndicatorsIcon />,
      link: ROUTES.INDICATORS,
      route: ROUTES.INDICATORS,
      subItems: [
        {
          title: 'Показатели поездов',
          link: ROUTES.INDICATORS_TRAIN_LIST,
          route: ROUTES.INDICATORS_TRAIN_LIST,
        },
        {
          title: 'Показатели групп вагонов',
          link: ROUTES.INDICATORS_WAGON_GROUPS,
          route: ROUTES.INDICATORS_WAGON_GROUPS,
        },
        {
          title: 'Расчеты локомотивного обслуживания',
          link: ROUTES.INDICATORS_LOCOMOTIVE,
          route: ROUTES.INDICATORS_LOCOMOTIVE,
        },
        {
          title: 'Расчеты аренды МВПС',
          link: ROUTES.INDICATORS_MVPS,
          route: ROUTES.INDICATORS_MVPS,
        },
        {
          title: 'Расчеты инфраструктуры',
          link: ROUTES.INDICATORS_INFRASTRUCTURE,
          route: ROUTES.INDICATORS_INFRASTRUCTURE,
        },
      ],
    },
    {
      title: 'Контроль параметров',
      icon: <ControlTransportParameterIcon />,
      link: ROUTES.CONTROL_TRANSPORT_PARAMETER,
      route: ROUTES.CONTROL_TRANSPORT_PARAMETER,
      subItems: [
        {
          title: 'Контроль наличия свободных мест по направлениям',
          link: ROUTES.TPC__CONTROL_AVAILABILITY_SEATS,
          route: ROUTES.TPC__CONTROL_AVAILABILITY_SEATS,
        },
        // TODO: временно убрали ссылку на старый контроль схем
        // {
        //   title: 'Контроль схем',
        //   link: ROUTES.CONTROL_TRANSPORT_PARAMETER_SCHEME_CONTROL,
        //   route: ROUTES.CONTROL_TRANSPORT_PARAMETER_SCHEME_CONTROL,
        // },
        {
          title: 'Контроль схем',
          link: ROUTES.CONTROL_TRANSPORT_PARAMETER_SCHEME_CONTROL_TRAIN_NMRVAG,
          route: ROUTES.CONTROL_TRANSPORT_PARAMETER_SCHEME_CONTROL_TRAIN_NMRVAG,
        },
        {
          title:
            'Контроль фактического местоположения вагонов, переданных в аренду',
          link: ROUTES.CONTROL_TRANSPORT_PARAMETER_LOCATION_CARRIAGE_GROUP,
          route: ROUTES.CONTROL_TRANSPORT_PARAMETER_LOCATION_CARRIAGE_GROUP,
        },
      ],
    },
    {
      title: 'Классификаторы',
      icon: <ClassifiersIcon />,
      link: ROUTES.DICTIONARIES,
      route: ROUTES.DICTIONARIES,
      subItems: [
        {
          title: 'Сеть/Поезда',
          link: ROUTES.DICTIONARIES_MESH_TRAINS,
          route: ROUTES.DICTIONARIES_MESH_TRAINS,
        },
        {
          title: 'Вагоны',
          link: ROUTES.DICTIONARIES_CARRIAGE_GROUPS,
          route: ROUTES.DICTIONARIES_CARRIAGE_GROUPS,
        },
        {
          title: 'Локомотивное обслуживание',
          link: ROUTES.DICTIONARIES_LOCOMOTIVE_SERVICE,
          route: ROUTES.DICTIONARIES_LOCOMOTIVE_SERVICE,
        },
        {
          title: 'Инфраструктура',
          link: ROUTES.DICTIONARIES_INFRASTRUCTURE,
          route: ROUTES.DICTIONARIES_INFRASTRUCTURE,
        },
        {
          title: 'МВПС',
          link: ROUTES.DICTIONARIES_MVPS,
          route: ROUTES.DICTIONARIES_MVPS,
        },
        {
          title: 'Контроль параметров перевозок',
          link: ROUTES.DICTIONARIES_CONTROL_TRANSPORTATION_PARAMETERS,
          route: ROUTES.DICTIONARIES_CONTROL_TRANSPORTATION_PARAMETERS,
        },
        {
          title: 'Справочники',
          link: ROUTES.MNEMONIC,
          route: ROUTES.MNEMONIC,
        },
      ],
    },
  ];
};
